<script setup lang="ts">
import { Ref, ref, reactive, onMounted, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf, minValue, helpers } from '@vuelidate/validators';
import { useDisciplineStore, PersonStores, useServicesStore } from '@/store';
import Dialog from 'primevue/dialog';
import Calendar from 'primevue/calendar';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';

import { FREQUENCY_INTERVALS_OPTIONS, FREQUENCY_AMOUNT } from '@/enum';
import {
  UserTreatmentPlansStateInterface,
  TreatmentPlanTypeGenericInterface,
  TreatmentPlanServiceCreationInterface,
} from '@/types';
import { ServiceInterface } from '@/types/Service';

const emit = defineEmits<{
  (e: 'save', form: TreatmentPlanServiceCreationInterface): void;
  (e: 'cancel'): void;
}>();

const newService = reactive<TreatmentPlanServiceCreationInterface>({
  treatment_service_type_id: 0,
  discipline_id: 0,
  start_at: undefined,
  end_at: undefined,
  duration: 30,
  treatment_frequency_type: { id: 0, acronym: '' },
  frequency: undefined,
  interval: undefined,
  service_id: 0,
  is_group: false,
});

const disciplineStore = useDisciplineStore();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const connectionStore = PersonStores.useUserConnectionsStore();
const servicesStore = useServicesStore();
const disciplines: Ref<{ id: number; name: string }[]> = ref([]);
const frequencyTypes: Ref<UserTreatmentPlansStateInterface[]> = ref([]);
const serviceTypes: Ref<TreatmentPlanTypeGenericInterface[]> = ref([]);
const services: Ref<ServiceInterface[]> = ref([]);
const modalVisible = ref(true);
const customFrequencySelected = computed(() => newService.treatment_frequency_type?.acronym === 'CUSTOM');
const frequencyIntervals = FREQUENCY_INTERVALS_OPTIONS;
const frequencyAmount = FREQUENCY_AMOUNT;

const frequencyTypeRequired = (value: TreatmentPlanServiceCreationInterface['treatment_frequency_type']) => !!value?.id;

const rules = computed(() => {
  return {
    treatment_service_type_id: {
      required: helpers.withMessage('Please select Service Type', minValue(1)),
    },
    discipline_id: {
      minValue: helpers.withMessage('Please select Discipline', minValue(1)),
    },
    start_at: {
      required: helpers.withMessage('Please choose Start Date', required),
    },
    end_at: {
      required: helpers.withMessage('Please choose End Date', required),
      minValue: helpers.withMessage(
        'Please choose a date greater than or equal to Start Date',
        minValue(newService.start_at as string),
      ),
    },
    treatment_frequency_type: {
      required: helpers.withMessage('Please select Frequency', frequencyTypeRequired),
    },
    duration: { required: helpers.withMessage('Please choose Duration', required) },
    frequency: {
      requiredIfCustom: helpers.withMessage('Please select Frequency', requiredIf(customFrequencySelected.value)),
    },
    interval: {
      requiredIfCustom: helpers.withMessage('Please select Interval', requiredIf(customFrequencySelected.value)),
    },
    service_id: {
      required: helpers.withMessage('Please select Service', minValue(1)),
    },
  };
});

const v$ = useVuelidate(rules, newService, { $autoDirty: true });

onMounted(async () => {
  await connectionStore.getConnections();
  await fetchDataForForm();
});

const fetchDataForForm = async () => {
  let organizationId = null;
  if (connectionStore.$state.connections.organizations?.length) {
    organizationId = connectionStore.$state.connections.organizations[0].id;
  }
  disciplines.value = (await disciplineStore.getDisciplinesOfOrganizationForDropdown(organizationId as number)) as [];
  frequencyTypes.value = (await treatmentPlansStore.getTreatmentPlanFrequencyTypes()) as [];
  serviceTypes.value = await treatmentPlansStore.getTreatmentServiceTypes();
  await servicesStore.getOrganizationServices(organizationId as number);
  newService.treatment_service_type_id = serviceTypes.value.find((type) => type.acronym === 'REG')?.id as number;
  services.value = servicesStore.availableServices as [];
};

const saveAttempted = ref(false);

const save = () => {
  saveAttempted.value = true;
  v$.value.$touch();
  if (!v$.value.$invalid) {
    emit('save', newService);
  }
  return;
};

const cancel = () => {
  emit('cancel');
  modalVisible.value = false;
  return;
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    :draggable="false"
    class="dialog__container new-event-modal"
    :modal="true"
    @hide="cancel"
  >
    <template #header>
      <h2 v-text="'Add New Service'" />
    </template>
    <template #default>
      <form class="p-4 pt-0 surface-100" :class="{ 'hide-input-errors': !saveAttempted }">
        <div class="field required">
          <label for="discipline">Discipline</label>
          <Dropdown
            id="discipline"
            v-model="newService.discipline_id"
            class="w-full"
            required
            :options="disciplines"
            option-value="id"
            option-label="name"
            placeholder="Select Discipline"
            :class="{ 'p-invalid': v$.discipline_id.$invalid && v$.discipline_id.$dirty }"
            @blur="v$.discipline_id.$touch"
          />
          <div v-for="error of v$.discipline_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="formgrid flex">
          <div class="field col required p-0 pr-2 flex flex-column">
            <label for="start_at">Start Date</label>
            <Calendar
              id="start_at"
              v-model="newService.start_at"
              placeholder="mm/dd/yyyy"
              autocomplete="off"
              :max-date="newService.end_at"
              :class="{ 'p-invalid': v$.start_at.$invalid && v$.start_at.$dirty }"
              @blur="v$.start_at.$touch"
            />
            <div v-for="error of v$.treatment_frequency_type.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
          <div class="field col required p-0 pl-2 flex flex-column">
            <label for="end_at">End Date</label>
            <Calendar
              id="end_at"
              v-model="newService.end_at"
              placeholder="mm/dd/yyyy"
              :min-date="newService.start_at"
              :class="{ 'p-invalid': v$.end_at.$invalid && v$.end_at.$dirty }"
              @blur="v$.end_at.$touch"
            />
            <div v-for="error of v$.duration.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
        <div class="field required">
          <label for="frequency">Frequency</label>
          <Dropdown
            id="frequency"
            v-model="newService.treatment_frequency_type"
            class="w-full"
            required
            :options="frequencyTypes"
            :option-value="
              (opt) => {
                return { id: opt.id, acronym: opt.acronym };
              }
            "
            option-label="title"
            placeholder="Select Frequency"
            :class="{ 'p-invalid': v$.treatment_frequency_type.$invalid && v$.treatment_frequency_type.$dirty }"
            @blur="v$.treatment_frequency_type.$touch"
          />
          <div v-for="error of v$.treatment_frequency_type.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div v-if="customFrequencySelected" class="formgrid flex">
          <div class="field col required p-0 pr-2">
            <label for="interval">Interval</label>
            <Dropdown
              id="interval"
              v-model="newService.interval"
              required
              :options="frequencyIntervals"
              option-value="value"
              option-label="label"
              placeholder="Select Interval"
              :class="{ 'p-invalid': v$.interval.$invalid && v$.interval.$dirty, 'w-full': true }"
              @blur="v$.interval.$touch"
            />
            <div v-for="error of v$.interval.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
          <div class="field col required p-0 pl-2">
            <label for="frequency_amount">Frequency</label>
            <Dropdown
              id="frequency_amount"
              v-model="newService.frequency"
              required
              :options="frequencyAmount"
              option-value="value"
              option-label="label"
              placeholder="Select Frequency"
              :class="{ 'p-invalid': v$.frequency.$invalid && v$.frequency.$dirty, 'w-full': true }"
              @blur="v$.frequency.$touch"
            />
            <div v-for="error of v$.frequency.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
        <div class="field required flex flex-column">
          <label for="duration">Duration (Minutes)</label>
          <InputNumber
            id="duration"
            v-model="newService.duration"
            :min="0"
            :class="{ 'p-invalid': v$.duration.$invalid && v$.duration.$dirty }"
            @blur="v$.duration.$touch"
          />
          <div v-for="error of v$.duration.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field flex flex-column">
          <label for="can_group">Can Group</label>
          <InputSwitch id="can_group" v-model="newService.is_group" />
        </div>
        <div class="field required">
          <label for="service">Service</label>
          <Dropdown
            id="service"
            v-model="newService.service_id"
            class="w-full"
            required
            :options="services"
            option-value="id"
            option-label="name"
            placeholder="Select Service"
            :class="{ 'p-invalid': v$.service_id.$invalid && v$.service_id.$dirty }"
            @blur="v$.service_id.$touch"
          />
          <div v-for="error of v$.service_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field required">
          <label for="service_type">Service Type</label>
          <Dropdown
            id="service_type"
            v-model="newService.treatment_service_type_id"
            class="w-full"
            required
            :options="serviceTypes"
            option-value="id"
            option-label="title"
            placeholder="Select Service Type"
            :class="{ 'p-invalid': v$.treatment_service_type_id.$invalid && v$.treatment_service_type_id.$dirty }"
            @blur="v$.treatment_service_type_id.$touch"
          />
          <div v-for="error of v$.treatment_service_type_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <AppButton id="cancel_service_btn" type="secondary" text="Cancel" @click="cancel" />
      <AppButton id="save_service_btn" type="primary" text="Add Service" @click="save" />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.field {
  margin-bottom: 1.5rem;
}
.hide-input-errors {
  .input-errors {
    display: none;
  }
  :deep(.p-invalid.p-component) {
    border-color: $grayLight;
    & > .p-inputtext {
      border-color: $grayLight;
      &:focus {
        border-color: var(--primary-color);
      }
    }
  }
}
</style>
